import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { saleBlock } from "@/lib/saleStatus";
import { authHeader } from "@/lib/token";
import { Button } from "@/components/ui/button";
import { ArrowUpCircle, BadgeCheck, Gift, ImagePlus, Loader2, Sparkles, Star, X } from "lucide-react";

const euro = (value) => `${(Number(value) || 0).toFixed(2).replace(".", ",")} €`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "");

export function youtubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return match ? match[1] : null;
}

/** Vidéos de présentation livrées avec le site (frontend/videos), utilisées quand
 *  le produit n'a pas sa propre vidéo dans le panel admin. */
export const SITE_VIDEO = { url: "/videos/vantapulse-presentation.mp4", poster: "/videos/vantapulse-presentation.jpg" };
const DEFAULT_VIDEO_IDS = ["vantaphone-yphone", "vantaphone-tablette", "vantaphone-pack", "gestion", "radio", "vantaguard"];
export function defaultProductVideo(productId) {
  return DEFAULT_VIDEO_IDS.includes(productId) ? `/videos/${productId}.mp4` : "";
}
const isSiteVideo = (url) => /^\/videos\/[a-z0-9-]+\.mp4$/.test(url || "");
const posterFor = (url) => (isSiteVideo(url) ? url.replace(/\.mp4$/, ".jpg") : undefined);

/** Lecteur vidéo : les vidéos du site existent en WebM (VP9) et en MP4 (H.264),
 *  le navigateur prend le premier format qu'il sait lire. */
export function VideoPlayer({ url, className = "", preload = "metadata", testId }) {
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video poster={posterFor(url)} controls playsInline preload={preload} className={className} data-testid={testId}>
      {isSiteVideo(url) && <source src={url.replace(/\.mp4$/, ".webm")} type="video/webm" />}
      <source src={url} type={isSiteVideo(url) || /\.mp4($|\?)/.test(url) ? "video/mp4" : undefined} />
    </video>
  );
}

/** Vidéo de démonstration : YouTube (sans cookies) ou fichier vidéo. */
export function ProductVideo({ url, title, heading = "Vidéo de présentation" }) {
  if (!url) return null;
  const id = youtubeId(url);
  return (
    <div data-testid="product-video">
      {heading && <h2 className="font-heading text-2xl font-bold mb-4">{heading}</h2>}
      <div className="relative w-full overflow-hidden rounded-3xl border border-white/[0.08] bg-black" style={{ aspectRatio: "16 / 9" }}>
        {id ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${id}?rel=0`}
            title={`Démonstration ${title || ""}`}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <VideoPlayer url={url} className="absolute inset-0 w-full h-full" />
        )}
      </div>
    </div>
  );
}

/** Essai gratuit (durée réglée dans le panel admin) : script téléchargeable
 *  (clé de licence d'essai) ou bot Discord (nom + token du bot). */
export function TrialCard({ product, user, onNeedAuth }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [botName, setBotName] = useState("");
  const [token, setToken] = useState("");
  const days = Number(product?.trial_days) || 0;
  // En maintenance / hors service : pas d'essai non plus.
  if (!product || days <= 0 || saleBlock(product)) return null;
  const isScript = !!product.downloadable;

  const start = async () => {
    if (!user) { onNeedAuth && onNeedAuth(); return; }
    if (!isScript && !showForm) { setShowForm(true); return; }
    if (!isScript && (!botName.trim() || !token.trim())) { toast.error("Indique le nom du bot et son token Discord"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/trials/${product.id}`, isScript ? {} : { bot_name: botName.trim(), discord_token: token.trim() }, { headers: authHeader() });
      toast.success(isScript
        ? "Essai activé : votre clé de licence et le téléchargement sont dans votre tableau de bord"
        : "Essai activé : votre bot démarre, retrouvez-le dans votre tableau de bord");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de démarrer l'essai");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="trial-card" className="rounded-2xl border border-emerald-400/25 bg-gradient-to-r from-emerald-500/[0.09] to-emerald-500/[0.02] p-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-300"><Gift className="h-5 w-5" /></span>
        <div className="flex-1">
          <p className="font-heading font-bold text-lg">Essayez gratuitement pendant {days} jour{days > 1 ? "s" : ""}</p>
          <p className="text-white/55 text-sm">
            {isScript
              ? "Sans carte bancaire. Une clé de licence d'essai, le même fichier que les clients. Si vous achetez ensuite, votre clé reste la même."
              : "Sans carte bancaire. Votre bot tourne avec toutes les fonctions pendant l'essai. Un seul essai par bot Discord."}
          </p>
        </div>
        {!showForm && (
          <Button data-testid="start-trial-btn" onClick={start} disabled={loading} className="h-11 bg-emerald-500 hover:bg-emerald-400 text-white rounded-xl px-5 font-semibold shrink-0">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Démarrer l'essai"}
          </Button>
        )}
      </div>
      {showForm && (
        <div className="mt-4 grid sm:grid-cols-[1fr_1.4fr_auto] gap-3 items-end" data-testid="trial-discord-form">
          <label className="block">
            <span className="text-xs text-text-muted">Nom du bot</span>
            <input data-testid="trial-bot-name" value={botName} onChange={(e) => setBotName(e.target.value)} placeholder="Mon bot" className="mt-1 w-full h-10 px-3 rounded-xl bg-black/30 border border-white/10 text-sm text-white focus:outline-none focus:border-emerald-500" />
          </label>
          <label className="block">
            <span className="text-xs text-text-muted">Token Discord du bot</span>
            <input data-testid="trial-bot-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="MTIzNDU2Nzg5MDEyMzQ1Njc4OQ..." className="mt-1 w-full h-10 px-3 rounded-xl bg-black/30 border border-white/10 text-sm text-white font-mono focus:outline-none focus:border-emerald-500" />
          </label>
          <Button data-testid="confirm-trial-btn" onClick={start} disabled={loading} className="h-10 bg-emerald-500 hover:bg-emerald-400 text-white rounded-xl px-6 font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Lancer l'essai"}
          </Button>
          <p className="sm:col-span-3 text-xs text-text-muted">Le token est chiffré et sécurisé. Ne le partagez jamais.</p>
        </div>
      )}
    </div>
  );
}

/** Passage au pack pour les clients qui ont déjà un produit du pack à vie. */
export function UpgradeCard({ product, user }) {
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    if (!user || !product?.includes?.length) { setQuote(null); return; }
    axios.get(`${API}/upgrades/${product.id}/quote`, { headers: authHeader() })
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null));
  }, [user, product]);
  if (!quote?.eligible || saleBlock(product)) return null;
  return (
    <div data-testid="upgrade-card" className="rounded-2xl border border-blue-400/30 bg-gradient-to-r from-blue-500/[0.1] to-violet-500/[0.04] p-5 flex flex-col sm:flex-row sm:items-center gap-4">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-500/15 text-blue-300"><ArrowUpCircle className="h-5 w-5" /></span>
      <div className="flex-1">
        <p className="font-heading font-bold text-lg">
          Passez au pack pour {euro(quote.final_price)} <span className="text-text-muted line-through text-base font-normal ml-1">{euro(quote.price)}</span>
        </p>
        <p className="text-white/55 text-sm">
          Vous avez déjà {quote.components.map((c) => c.name).join(", ")} à vie : {euro(quote.credit)} déduits. La clé déjà utilisée par votre serveur devient celle du pack, rien à changer dans la config.
        </p>
      </div>
      <Button data-testid="upgrade-btn" onClick={() => navigate(`/checkout?plan=lifetime&bot=${product.id}&upgrade=1`)} className="h-11 bg-blue-500 hover:bg-blue-400 text-white rounded-xl px-5 font-semibold shrink-0">
        Passer au pack
      </Button>
    </div>
  );
}

/** Historique des versions (« Nouveautés »). */
export function ProductChangelog({ versions }) {
  const [showAll, setShowAll] = useState(false);
  if (!versions?.length) return null;
  const shown = showAll ? versions : versions.slice(0, 3);
  return (
    <div data-testid="product-changelog">
      <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-300" />Nouveautés</h2>
      <ol className="relative border-l border-white/10 ml-2 space-y-6">
        {shown.map((v, index) => (
          <li key={`${v.version}-${index}`} className="ml-6">
            <span className={`absolute -left-[7px] mt-1.5 w-3.5 h-3.5 rounded-full border-2 border-[#0f1019] ${index === 0 ? "bg-violet-400" : "bg-white/20"}`} />
            <p className="font-heading font-bold">
              Version {v.version}
              {index === 0 && <span className="ml-2 rounded-full text-[11px] font-medium px-2 py-0.5 bg-violet-500/15 text-violet-200">Actuelle</span>}
            </p>
            <p className="text-white/35 text-xs mb-1">{fmtDate(v.date)}</p>
            {v.notes ? <p className="text-white/55 text-sm leading-relaxed whitespace-pre-line">{v.notes}</p> : <p className="text-white/35 text-sm">Mise à jour du fichier.</p>}
          </li>
        ))}
      </ol>
      {versions.length > 3 && (
        <button onClick={() => setShowAll(!showAll)} className="mt-5 text-sm text-violet-300 hover:text-violet-200">
          {showAll ? "Voir moins" : `Voir les ${versions.length} versions`}
        </button>
      )}
    </div>
  );
}

function Stars({ value, size = "w-4 h-4", onChange }) {
  return (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          onClick={onChange ? () => onChange(n) : undefined}
          className={`${size} ${value >= n ? "text-yellow-400 fill-yellow-400" : "text-white/15"} ${onChange ? "cursor-pointer" : ""}`}
        />
      ))}
    </div>
  );
}

const MAX_SCREENSHOT_SIZE = 2 * 1024 * 1024;

/** Avis clients d'un produit, avec captures d'écran. */
export function ProductReviews({ productId, user, onNeedAuth }) {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ count: 0, average: 0 });
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/reviews`, { params: { product_id: productId } }).then((r) => setReviews(r.data || [])).catch(() => {});
    axios.get(`${API}/reviews/stats`, { params: { product_id: productId } }).then((r) => setStats(r.data || { count: 0, average: 0 })).catch(() => {});
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const addImage = (file) => {
    if (!file) return;
    if (images.length >= 3) { toast.error("3 captures d'écran maximum"); return; }
    if (file.size > MAX_SCREENSHOT_SIZE) { toast.error("Image trop lourde (2 Mo max)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const r = await axios.post(`${API}/upload/image`, { image: reader.result }, { headers: authHeader() });
        setImages((current) => [...current, r.data.url].slice(0, 3));
      } catch {
        toast.error("Envoi de l'image impossible");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, { product_id: productId, rating, title, comment, images }, { headers: authHeader() });
      toast.success("Merci pour votre avis !");
      setShowForm(false); setTitle(""); setComment(""); setImages([]); setRating(5);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'enregistrer l'avis");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="product-reviews">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading text-xl font-bold">Avis clients</h2>
          {stats.count > 0 ? (
            <div className="flex items-center gap-2 mt-2"><Stars value={Math.round(stats.average)} /><span className="text-white/55 text-sm">{stats.average.toFixed(1)} / 5 · {stats.count} avis</span></div>
          ) : (
            <p className="text-white/40 text-sm mt-1">Pas encore d'avis sur ce produit. Sois le premier à donner le tien !</p>
          )}
        </div>
        {!showForm && (
          <Button data-testid="write-review-btn" onClick={() => (user ? setShowForm(true) : onNeedAuth && onNeedAuth())} variant="outline" className="rounded-xl border-white/15 bg-white/[0.03] hover:bg-white/[0.08]">
            Laisser un avis
          </Button>
        )}
      </div>

      {showForm && (
        <div className="rounded-2xl bg-black/20 border border-white/[0.08] p-5 mb-6 space-y-3" data-testid="review-form">
          <p className="text-text-muted text-xs">Réservé aux clients qui ont acheté ce produit.</p>
          <Stars value={rating} size="w-6 h-6" onChange={setRating} />
          <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 80))} maxLength={80} placeholder="Titre de ton avis (ex. : Parfait pour mon serveur RP)" className="w-full rounded-xl bg-black/30 border border-white/10 p-3 text-sm text-white focus:outline-none focus:border-violet-500" data-testid="review-title" />
          <textarea value={comment} onChange={(e) => setComment(e.target.value.slice(0, 2000))} rows={4} placeholder="Qu'avez-vous pensé du produit ?" className="w-full rounded-xl bg-black/30 border border-white/10 p-3 text-sm text-white focus:outline-none focus:border-violet-500" />
          <div className="flex flex-wrap items-center gap-2">
            {images.map((src) => (
              <div key={src} className="relative">
                <img src={src} alt="" className="w-20 h-14 rounded-lg object-cover border border-white/10" />
                <button onClick={() => setImages(images.filter((i) => i !== src))} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
              </div>
            ))}
            {images.length < 3 && (
              <label className="w-20 h-14 rounded-lg border border-dashed border-white/15 flex items-center justify-center cursor-pointer hover:border-blue-500 text-text-muted" title="Ajouter une capture d'écran">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { addImage(e.target.files[0]); e.target.value = ""; }} />
              </label>
            )}
            <span className="text-text-muted text-xs">Jusqu'à 3 captures d'écran de votre serveur</span>
          </div>
          <div className="flex gap-2">
            <Button data-testid="submit-review-btn" onClick={submit} disabled={sending || uploading} className="rounded-xl bg-violet-600 hover:bg-violet-500">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publier"}</Button>
            <Button variant="ghost" onClick={() => setShowForm(false)} className="rounded-xl">Annuler</Button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {reviews.map((r) => (
          <div key={r.id} className="rounded-2xl bg-white/[0.02] border border-white/[0.06] p-5">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <Stars value={r.rating} />
              <span className="font-semibold text-sm">{r.username || "Client"}</span>
              {r.verified && <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><BadgeCheck className="w-3.5 h-3.5" />Achat vérifié</span>}
              <span className="text-text-muted text-xs">{fmtDate(r.created_at)}</span>
            </div>
            {r.title && <p className="font-semibold text-sm mb-1">{r.title}</p>}
            {r.comment && <p className="text-white/60 text-sm leading-relaxed whitespace-pre-line">{r.comment}</p>}
            {r.images?.length > 0 && (
              <div className="flex gap-2 mt-3">
                {r.images.map((src) => (
                  <a key={src} href={src} target="_blank" rel="noreferrer"><img src={src} alt="Capture d'écran du client" className="w-28 h-20 rounded-lg object-cover border border-white/10 hover:border-violet-400" /></a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
